import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, HStack, StackItem } from "@astryxdesign/core/Stack";
import { Grid } from "@astryxdesign/core/Grid";
import { Text, Heading } from "@astryxdesign/core/Text";
import { Card } from "@astryxdesign/core/Card";
import { ClickableCard } from "@astryxdesign/core/ClickableCard";
import { SelectableCard } from "@astryxdesign/core/SelectableCard";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Icon } from "@astryxdesign/core/Icon";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "@astryxdesign/core/SegmentedControl";
import { Breadcrumbs, BreadcrumbItem } from "@astryxdesign/core/Breadcrumbs";
import FileViewer from "./FileViewer";
import FolderTree from "./FolderTree";
import DownloadProgressModal from "./DownloadProgressModal";
import { downloadsAPI } from "../../services/api";
import AstryxTheme from "../../theme/AstryxTheme";

// Estrutura inicial de fallback caso o backend não retorne dados
const initialFileStructure = {
  id: "root",
  name: "Notas Fiscais",
  type: "folder",
  children: {},
};

export default function FileManager() {
  const [currentPath, setCurrentPath] = useState([]);
  const [fileStructure, setFileStructure] = useState({});
  const [viewMode, setViewMode] = useState("grid"); // grid ou list
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  // Folder download states
  const [selectedFolders, setSelectedFolders] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [currentDownloadFile, setCurrentDownloadFile] = useState("");
  const [downloadingFolder, setDownloadingFolder] = useState("");
  const [downloadError, setDownloadError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  // Filter states
  const [search, setSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    // Load root listing from backend (Supabase bucket root)
    const loadRoot = async () => {
      try {
        const result = await downloadsAPI.listFiles("");
        const rootChildren = {};
        // Backend returns array with { name, id?, type: 'file'|'folder'?, ... }
        // Supabase .list returns objects with name and metadata; use that
        (result.files || []).forEach((entry) => {
          const isFolder =
            entry.id === null || entry.metadata?.mimetype === undefined;
          const displayName = entry.name;
          const originalName = entry.originalName || entry.name;

          const size = entry.metadata?.size || entry.size || null;
          const createdAt =
            entry.metadata?.created_at ||
            entry.created_at ||
            entry.createdAt ||
            null;
          const mimeType = entry.metadata?.mimetype || entry.mimetype || null;
          // Get emission date from database metadata
          const emissionDate = entry.emission_date || null;

          rootChildren[displayName] = {
            id: `root-${originalName}`,
            name: displayName,
            originalName: originalName,
            type: isFolder ? "folder" : "file",
            size: isFolder ? undefined : size,
            date: isFolder ? undefined : createdAt || null,
            emissionDate: isFolder ? undefined : emissionDate || null,
            mimeType: isFolder ? undefined : mimeType,
            children: isFolder ? {} : undefined,
          };
        });

        setFileStructure({
          id: "root",
          name: "Notas Fiscais",
          type: "folder",
          children: rootChildren,
        });
      } catch (e) {
        console.error("Failed to load root files:", e);
        setFileStructure(initialFileStructure);
      }
    };
    loadRoot();
  }, []);

  // Navegar para uma pasta
  const navigateToFolder = (folderName, originalName = null) => {
    // Use originalName for navigation if available, otherwise use folderName
    const pathName = originalName || folderName;
    setCurrentPath((prev) => [...prev, pathName]);
  };

  // Navegar para pasta específica no breadcrumb
  const navigateToBreadcrumb = (index) => {
    setCurrentPath((prev) => prev.slice(0, index + 1));
  };

  // Obter pasta atual baseada no caminho
  const getCurrentFolder = () => {
    let current = fileStructure;
    for (const pathSegment of currentPath) {
      if (current.children && current.children[pathSegment]) {
        current = current.children[pathSegment];
      } else {
        return null;
      }
    }
    return current;
  };

  // Detectar se estamos no nível de pastas de empresas
  const isCompanyFolderLevel = () => {
    const current = getCurrentFolder();
    if (!current) return false;

    // Verificar se estamos em "Servico Tomado" ou "Servico Prestado"
    if (currentPath.length === 1) {
      const parentFolder = currentPath[0];
      return (
        parentFolder === "Servico Tomado" || parentFolder === "Servico Prestado"
      );
    }

    // Verificar se estamos na raiz e há pastas de empresas
    if (currentPath.length === 0) {
      return Object.values(current.children || {}).some(
        (item) => item.type === "folder" && item.name.includes(" - ")
      );
    }

    return false;
  };

  // Filtrar pastas de empresas por código/nome
  const filterCompanyFolders = (items, searchTerm) => {
    if (!searchTerm.trim()) return items;

    const term = searchTerm.toLowerCase().trim();
    return items.filter((item) => {
      if (item.type !== "folder") return true; // Manter arquivos
      const folderName = item.name.toLowerCase();
      return folderName.includes(term);
    });
  };

  // Obter itens da pasta atual
  const getCurrentItems = () => {
    const current = getCurrentFolder();
    if (!current || !current.children) return [];

    let items = Object.values(current.children).map((item) => ({
      ...item,
      key: item.id,
    }));

    // Aplicar filtro se estiver no nível de empresas e houver termo de busca
    if (isCompanyFolderLevel() && isSearching && search) {
      items = filterCompanyFolders(items, search);
    }

    return items;
  };

  // Abrir pasta
  const openFolder = async (folderName, originalName = null) => {
    // Verificar se é uma pasta de nível raiz (empresa)
    const isRootLevelFolder =
      fileStructure.children && fileStructure.children[folderName];

    if (isRootLevelFolder) {
      // Se for uma pasta no nível atual, carregar seu conteúdo do backend
      const pathName = originalName || folderName; // Use originalName for navigation
      const newPath = [pathName];
      try {
        const pathQuery = newPath.join("/");
        const result = await downloadsAPI.listFiles(pathQuery);
        const folderChildren = {};
        (result.files || []).forEach((entry) => {
          const isFolder =
            entry.id === null || entry.metadata?.mimetype === undefined;
          const displayName = entry.name;
          const originalName = entry.originalName || entry.name;

          const size = entry.metadata?.size || entry.size || null;
          const createdAt =
            entry.metadata?.created_at ||
            entry.created_at ||
            entry.createdAt ||
            null;
          const mimeType = entry.metadata?.mimetype || entry.mimetype || null;
          // Get emission date from database metadata
          const emissionDate = entry.emission_date || null;

          folderChildren[displayName] = {
            id: `${pathQuery}-${originalName}`,
            name: displayName,
            originalName: originalName,
            type: isFolder ? "folder" : "file",
            size: isFolder ? undefined : size,
            date: isFolder ? undefined : createdAt || null,
            emissionDate: isFolder ? undefined : emissionDate || null,
            mimeType: isFolder ? undefined : mimeType,
            children: isFolder ? {} : undefined,
          };
        });
        setFileStructure((prev) => {
          const updated = JSON.parse(JSON.stringify(prev));
          updated.children[folderName].children = folderChildren;
          return updated;
        });
      } catch (e) {
        console.error("Failed to load folder:", e);
      }
      // resetar o caminho e navegar para ela (a árvore acompanha o caminho)
      setCurrentPath(newPath);
    } else {
      // Se for uma subpasta, verificar se já estamos no caminho correto
      const currentFolder = getCurrentFolder();
      if (
        currentFolder &&
        currentFolder.children &&
        currentFolder.children[folderName]
      ) {
        // Adicionar ao caminho atual e carregar conteúdo
        const newPath = [...currentPath, folderName];
        try {
          const pathQuery = newPath.join("/");
          const result = await downloadsAPI.listFiles(pathQuery);
          const folderChildren = {};
          (result.files || []).forEach((entry) => {
            const isFolder =
              entry.id === null || entry.metadata?.mimetype === undefined;
            const displayName = entry.name;
            const originalName = entry.originalName || entry.name;

            const size = entry.metadata?.size || entry.size || null;
            const createdAt =
              entry.metadata?.created_at ||
              entry.created_at ||
              entry.createdAt ||
              null;
            const mimeType = entry.metadata?.mimetype || entry.mimetype || null;
            const emissionDate =
              entry.metadata?.emission_date || entry.emission_date || null;

            folderChildren[displayName] = {
              id: `${pathQuery}-${originalName}`,
              name: displayName,
              originalName: originalName,
              type: isFolder ? "folder" : "file",
              size: isFolder ? undefined : size,
              date: isFolder ? undefined : createdAt || null,
              emissionDate: isFolder ? undefined : emissionDate || null,
              mimeType: isFolder ? undefined : mimeType,
              children: isFolder ? {} : undefined,
            };
          });
          setFileStructure((prev) => {
            const updated = JSON.parse(JSON.stringify(prev));
            // Traverse to target folder
            let node = updated;
            for (const seg of newPath.slice(0, -1)) {
              node = node.children[seg];
            }
            node.children[folderName].children = folderChildren;
            return updated;
          });
        } catch (e) {
          console.error("Failed to load subfolder:", e);
        }
        navigateToFolder(folderName, originalName);
      } else {
        // Se não encontrou a pasta no nível atual, tentar encontrar na estrutura
        // e navegar para o caminho correto
        const pathToFolder = findPathToFolder(folderName);
        if (pathToFolder) {
          setCurrentPath(pathToFolder);
        }
      }
    }
  };

  // Função auxiliar para encontrar o caminho para uma pasta específica
  const findPathToFolder = (
    folderName,
    structure = fileStructure,
    currentPath = []
  ) => {
    if (!structure.children) return null;

    for (const [name, item] of Object.entries(structure.children)) {
      if (name === folderName) {
        return [...currentPath, name];
      }

      if (item.children) {
        const found = findPathToFolder(folderName, item, [
          ...currentPath,
          name,
        ]);
        if (found) return found;
      }
    }

    return null;
  };

  // Abrir arquivo
  const openFile = (file) => {
    setSelectedFile(file);
    setIsFileViewerOpen(true);
  };

  // Formatar data com fallback
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("pt-BR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Função para formatar tamanho do arquivo com unidades
  const formatFileSize = (size) => {
    if (size === undefined || size === null) return "-";
    const n = Number(size);
    if (Number.isNaN(n)) return String(size);
    const units = ["B", "KB", "MB", "GB", "TB"];
    let i = 0;
    let val = n;
    while (val >= 1024 && i < units.length - 1) {
      val /= 1024;
      i++;
    }
    return `${val.toFixed(val < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
  };

  // Função para download real do arquivo
  const downloadFile = async (file) => {
    try {
      // Build path from breadcrumb and file name (e.g., companyId/YYYY/MM/filename)
      const pathSegments = [...currentPath, file.name];
      const storagePath = pathSegments.join("/");
      const result = await downloadsAPI.getSignedUrl(storagePath, 3600);
      if (result && result.url) {
        const link = document.createElement("a");
        link.href = result.url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Signed URL not returned for", storagePath);
      }
    } catch (e) {
      console.error("Failed to download file:", e);
    }
  };

  // Toggle selection mode
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedFolders(new Set());
  };

  // Toggle folder selection
  const toggleFolderSelection = (folderPath) => {
    const newSelected = new Set(selectedFolders);
    if (newSelected.has(folderPath)) {
      newSelected.delete(folderPath);
    } else {
      newSelected.add(folderPath);
    }
    setSelectedFolders(newSelected);
  };

  // Check if folder is selected
  const isFolderSelected = (folderPath) => {
    return selectedFolders.has(folderPath);
  };

  // Download single folder
  const downloadFolder = async (folderName, originalName = null) => {
    try {
      const folderPath = originalName || folderName;
      const fullPath =
        currentPath.length > 0
          ? [...currentPath, folderPath].join("/")
          : folderPath;

      // Check folder size first
      const folderInfo = await downloadsAPI.getFolderInfo(fullPath);
      const isLargeFolder =
        folderInfo.fileCount > 50 || folderInfo.totalSize > 100 * 1024 * 1024; // > 100MB

      // Show progress modal for large folders
      if (isLargeFolder) {
        setShowProgressModal(true);
        setDownloadingFolder(folderName);
        setDownloadProgress(0);
        setCurrentDownloadFile("");
        setDownloadError(null);
        setIsDownloading(true);
      }

      // Start download
      const result = await downloadsAPI.downloadFolderZip(fullPath);

      if (result.success) {
        if (isLargeFolder) {
          setDownloadProgress(100);
          setCurrentDownloadFile("");
          setIsDownloading(false);
          // Auto-close after 2 seconds
          setTimeout(() => {
            setShowProgressModal(false);
            setDownloadingFolder("");
          }, 2000);
        }
      } else {
        throw new Error(
          result.error || result.message || "Erro ao baixar pasta"
        );
      }
    } catch (error) {
      console.error("Failed to download folder:", error);
      setDownloadError(error.message || "Erro ao baixar pasta");
      setIsDownloading(false);
    }
  };

  // Download multiple selected folders
  const downloadSelectedFolders = async () => {
    if (selectedFolders.size === 0) {
      alert("Selecione pelo menos uma pasta para download");
      return;
    }

    try {
      const folderPaths = Array.from(selectedFolders).map((folderKey) => {
        // folderKey format: "path/to/folder"
        return folderKey;
      });

      // Show progress modal
      setShowProgressModal(true);
      setDownloadingFolder(`${selectedFolders.size} pastas selecionadas`);
      setDownloadProgress(0);
      setCurrentDownloadFile("");
      setDownloadError(null);
      setIsDownloading(true);

      // Start download
      const result = await downloadsAPI.downloadMultipleFolders(folderPaths);

      if (result.success) {
        setDownloadProgress(100);
        setCurrentDownloadFile("");
        setIsDownloading(false);
        // Clear selection and exit selection mode
        setSelectedFolders(new Set());
        setSelectionMode(false);
        // Auto-close after 2 seconds
        setTimeout(() => {
          setShowProgressModal(false);
          setDownloadingFolder("");
        }, 2000);
      } else {
        throw new Error(
          result.error || result.message || "Erro ao baixar pastas"
        );
      }
    } catch (error) {
      console.error("Failed to download folders:", error);
      setDownloadError(error.message || "Erro ao baixar pastas");
      setIsDownloading(false);
    }
  };

  // Close progress modal
  const closeProgressModal = () => {
    setShowProgressModal(false);
    setDownloadingFolder("");
    setDownloadProgress(0);
    setCurrentDownloadFile("");
    setDownloadError(null);
    setIsDownloading(false);
  };

  // Cancel download (currently only closes modal, full cancellation would require backend changes)
  const cancelDownload = () => {
    setDownloadError("Download cancelado pelo usuário");
    setIsDownloading(false);
    setTimeout(() => {
      closeProgressModal();
    }, 2000);
  };

  // Função de busca executada por Enter
  const executeSearch = (term) => {
    if (term.trim()) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
  };

  // Digitar só atualiza o valor; buscar é no Enter. Limpar desfaz a busca.
  const handleSearchChange = (value) => {
    setSearch(value);
    if (value === "" && isSearching) {
      setIsSearching(false);
    }
  };

  // Limpar filtro ao sair do nível de empresas
  useEffect(() => {
    const isCompanyLevel = isCompanyFolderLevel();
    if (!isCompanyLevel) {
      setSearch("");
      setIsSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, fileStructure]);

  const currentItems = getCurrentItems();
  const isRoot = currentPath.length === 0;

  const renderItemCard = (item) => {
    const folderFullPath =
      item.type === "folder"
        ? currentPath.length > 0
          ? [...currentPath, item.originalName || item.name].join("/")
          : item.originalName || item.name
        : null;
    const isSelected = folderFullPath && isFolderSelected(folderFullPath);

    const cardContent = (
      <HStack gap={2} vAlign="start" hAlign="between">
        <HStack gap={2} vAlign="start">
          <Text color={item.type === "folder" ? "accent" : "secondary"}>
            <FontAwesomeIcon
              icon={item.type === "folder" ? "folder" : "file-alt"}
              size="lg"
            />
          </Text>
          <Stack gap={0.5}>
            <Text weight="semibold" size="sm">
              {item.name}
            </Text>
            {item.type === "file" && (
              <Stack gap={0}>
                <Text type="supporting">
                  Tamanho: {formatFileSize(item.size)}
                </Text>
                <Text type="supporting">
                  Tipo: {item.file_type || item.mimeType || "-"}
                </Text>
                <Text type="supporting">Criação: {formatDate(item.date)}</Text>
                <Text type="supporting">
                  Emissão: {formatDate(item.emissionDate)}
                </Text>
              </Stack>
            )}
          </Stack>
        </HStack>
        {item.type === "folder" && !selectionMode && (
          <IconButton
            label="Baixar Pasta"
            tooltip="Baixar Pasta"
            icon={<FontAwesomeIcon icon="download" />}
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e?.stopPropagation?.();
              downloadFolder(item.name, item.originalName);
            }}
          />
        )}
        {item.type === "file" && (
          <IconButton
            label="Download"
            tooltip="Download"
            icon={<FontAwesomeIcon icon="download" />}
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e?.stopPropagation?.();
              downloadFile(item);
            }}
          />
        )}
      </HStack>
    );

    if (selectionMode && item.type === "folder") {
      return (
        <SelectableCard
          key={item.id}
          label={`Selecionar ${item.name}`}
          isSelected={isSelected || false}
          onChange={() => toggleFolderSelection(folderFullPath)}
          padding={3}
        >
          {cardContent}
        </SelectableCard>
      );
    }

    return (
      <ClickableCard
        key={item.id}
        label={item.type === "folder" ? `Abrir ${item.name}` : item.name}
        onClick={() => {
          if (item.type === "folder") {
            openFolder(item.name, item.originalName);
          } else {
            openFile(item);
          }
        }}
        padding={3}
      >
        {cardContent}
      </ClickableCard>
    );
  };

  return (
    <AstryxTheme>
      <Stack
        as="section"
        aria-label="Gerenciador de Notas Fiscais"
        gap={4}
        width="100%"
      >
        {/* Cabeçalho: título + ações */}
        <HStack gap={3} vAlign="center" hAlign="between" wrap="wrap">
          <HStack gap={2} vAlign="center">
            <Heading level={2}>Gerenciador de Notas Fiscais</Heading>
            <Text type="supporting">
              {currentItems.length}{" "}
              {currentItems.length === 1 ? "item" : "itens"}
            </Text>
          </HStack>
          <HStack gap={2} vAlign="center" wrap="wrap">
            {selectionMode && selectedFolders.size > 0 && (
              <Button
                label={`Baixar Selecionadas (${selectedFolders.size})`}
                variant="primary"
                icon={<FontAwesomeIcon icon="download" />}
                onClick={downloadSelectedFolders}
              />
            )}
            <Button
              label={selectionMode ? "Cancelar Seleção" : "Selecionar Pastas"}
              variant={selectionMode ? "secondary" : "ghost"}
              icon={
                <FontAwesomeIcon icon={selectionMode ? "times" : "check"} />
              }
              onClick={toggleSelectionMode}
            />
            <SegmentedControl
              label="Modo de visualização"
              value={viewMode}
              onChange={setViewMode}
              size="sm"
            >
              <SegmentedControlItem
                value="grid"
                label="Grade"
                isLabelHidden
                icon={<FontAwesomeIcon icon="th" />}
              />
              <SegmentedControlItem
                value="list"
                label="Lista"
                isLabelHidden
                icon={<FontAwesomeIcon icon="list" />}
              />
            </SegmentedControl>
          </HStack>
        </HStack>

        {/* Campo de busca - visível apenas no nível de empresas */}
        {isCompanyFolderLevel() && (
          <TextInput
            label="Pesquisar"
            isLabelHidden
            placeholder="Código ou nome da empresa (Enter para buscar)"
            value={search}
            onChange={handleSearchChange}
            onEnter={() => executeSearch(search)}
            startIcon={<Icon icon="search" />}
            hasClear
            width={360}
          />
        )}

        {/* Layout: árvore de pastas + conteúdo */}
        <HStack gap={3} vAlign="stretch" wrap="wrap">
          <Stack width={300}>
            <Card padding={3}>
              <Stack gap={2}>
                <HStack gap={2} vAlign="center">
                  <Text color="accent">
                    <FontAwesomeIcon icon="sitemap" />
                  </Text>
                  <Text weight="semibold">Estrutura de Pastas</Text>
                </HStack>
                <Stack isScrollable height={480}>
                  <FolderTree
                    fileStructure={fileStructure}
                    onFolderSelect={(folderName, originalName) =>
                      openFolder(folderName, originalName)
                    }
                    currentPath={currentPath}
                  />
                </Stack>
              </Stack>
            </Card>
          </Stack>

          <StackItem size="fill">
            <Card padding={4}>
              <Stack gap={3}>
                {/* Breadcrumb */}
                <Breadcrumbs variant="supporting">
                  <BreadcrumbItem
                    onClick={() => setCurrentPath([])}
                    isCurrent={isRoot}
                    startIcon={<FontAwesomeIcon icon="home" />}
                  >
                    Início
                  </BreadcrumbItem>
                  {currentPath.map((segment, index) => (
                    <BreadcrumbItem
                      key={index}
                      onClick={() => navigateToBreadcrumb(index)}
                      isCurrent={index === currentPath.length - 1}
                    >
                      {segment}
                    </BreadcrumbItem>
                  ))}
                </Breadcrumbs>

                {/* Conteúdo Principal */}
                <Stack isScrollable height={480}>
                  {currentItems.length === 0 ? (
                    <EmptyState
                      title={
                        isSearching && search
                          ? "Nenhuma pasta encontrada para a busca"
                          : isRoot
                            ? "Nenhuma empresa encontrada"
                            : "Pasta vazia"
                      }
                      description={
                        isSearching && search
                          ? `Nenhuma pasta corresponde ao termo "${search}". Tente buscar por código ou nome da empresa.`
                          : isRoot
                            ? "As empresas aparecerão aqui quando houver notas fiscais"
                            : "Esta pasta não contém arquivos ou subpastas"
                      }
                      icon={<FontAwesomeIcon icon="folder-open" />}
                    />
                  ) : viewMode === "grid" ? (
                    <Grid columns={{ minWidth: 250, max: 3 }} gap={3}>
                      {currentItems.map((item) => renderItemCard(item))}
                    </Grid>
                  ) : (
                    <Stack gap={2}>
                      {currentItems.map((item) => renderItemCard(item))}
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Card>
          </StackItem>
        </HStack>

        {/* File Viewer Modal */}
        {isFileViewerOpen && selectedFile && (
          <FileViewer
            file={selectedFile}
            onClose={() => {
              setIsFileViewerOpen(false);
              setSelectedFile(null);
            }}
          />
        )}

        {/* Download Progress Modal */}
        <DownloadProgressModal
          isOpen={showProgressModal}
          onClose={closeProgressModal}
          folderName={downloadingFolder}
          isDownloading={isDownloading}
          progress={downloadProgress}
          currentFile={currentDownloadFile}
          error={downloadError}
          onCancel={cancelDownload}
        />
      </Stack>
    </AstryxTheme>
  );
}
